/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { Component } from 'react';
import { bindActionCreators, compose } from 'redux';
import {
    object, shape, string, func,
} from 'prop-types';
import { connect } from 'react-redux';

// Material ui
import withStyles from '@material-ui/core/styles/withStyles';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import TypeChecking from '../../../../pages/Checkout/containers/CheckoutFloral/Partials/TextFieldWrapper/TypeChecking';

// redux and helpers
import { resetGiftMessageForm, handleChange } from '../../../../../state/ducks/Checkout/ducks/Recipient/ducks/GiftMessageForm/GiftMessageForm-Actions';
import { getSavedAddress, getDataDictionary } from '../../../../../state/ducks/App/App-Selectors';
import { getLanguageName } from '../../../../../state/ducks/Member/ducks/Auth/helper/helper';

// component
import OccasionsDialog from './OccasionsDialog';

const styles = (theme) => ({
    root: {
        width: '100%',
        color: '#000',
        background: theme.palette.white,
        position: 'relative',
        marginBottom: '20px',
    },
    checkbox: {
        color: '#693388',
        padding: '16px 11px',
        '&$checked': {
            color: '#693388',
        },
        [theme.breakpoints.down('600')]: {
            '& svg': {
                fontSize: '24px',
            },
        },
    },
    formCheckBoxLabel: {
        fontSize: '14px',
        lineHeight: '16px',
        color: theme.palette?.black || '#1f1f1b',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
        },
    },
    checked: {
        color: '#693388',
        '&$checked': {
            color: '#693388',
        },
    },
    messageHeader: {
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
        margin: '10px 0',
        position: 'absolute',
        right: 0,
        top: '5px',
    },
    paperBody: {
        boxSizing: 'border-box',
        padding: '10px 10px 12px 10px',
        border: `1px solid ${theme.palette?.pdp?.cardBorder}`,
        boxShadow: 'none',
        borderRadius: '3px',
    },
    textField: {
        width: '100%',
        height: '106px',
        border: 'none',
        '& label': {
            color: theme.palette?.pdp?.textFieldColor,
            bottom: 0,
            right: 0,
            top: 'auto',
            left: 'auto',
            transformOrigin: 'top right',
            [theme.breakpoints.down('600')]: {
                transformOrigin: 'bottom left',
                bottom: '-32px',
                left: '-8px',
            },
            '&+ div': {
                marginTop: 0,
                '&:before, &:after': {
                    border: 'none !important',
                },
                '& textarea': {
                    overflow: 'hidden',
                },
            },
        },
        '& label[class*="focused"]': {
            color: theme.palette.primary?.main,
        },
    },
    displayNone: {
        display: 'none',
    },
    displayBlock: {
        display: 'block',
        marginBottom: '30px',
        [theme.breakpoints.down('600')]: {
            marginBottom: '40px',
        },
    },

    // Suggested Messages
    suggestedMessagesLink: {
        fontSize: '12px',
        color: theme.palette.colorNeutral70,
        textDecoration: 'underline',
        cursor: 'pointer',
        display: 'none',
    },

    // Suggested Messages Dialog
    dialogPaperWidth: {
        maxWidth: '800px',
    },
    dialogWrapper: {
        display: 'flex',
        position: 'absolute',
        top: '0',
        height: '400px',
        transition: 'all 0.2s ease-in-out 0s',
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: theme.palette.colorPrimary,
        borderBottom: `2px solid ${theme.palette.colorPrimary}`,
        '& h3': {
            margin: '2px 0',
        },
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        outline: 0,
    },
});

class GraphqlPDPGiftMessage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            textField: '',
            characterCount: 0,
            maxCharacters: 200,
            showGiftMessage: false,
            showSuggestedMessages: false,
            occasionIndex: {},
            activePanel: 0,
            occasionCode: null,
        };
    }

    componentDidMount() {
        const { callResetGiftMessageForm, savedAddress: { addressSource } } = this.props;
        const {  textField } = this.state;
        callResetGiftMessageForm();

        if (addressSource === 'SCI' && !textField) {
            this.addDefaultMessage();
        }
    }

    handleChange = (event) => {
        const { callHandleChange } = this.props;
        const { maxCharacters } = this.state;
        let resultValue = event.target.value;
        if (resultValue.length <= maxCharacters) {
            // Review the restriction in the input
            const dataToCheck = { value: event.target.value };
            const restrictInput = TypeChecking.MultilineGiftMessage;
            if (restrictInput) {
                resultValue = (restrictInput) ? restrictInput(dataToCheck) : dataToCheck.value;
            }

            callHandleChange({ name: 'message', value: resultValue });

            this.setState({
                textField: resultValue,
                characterCount: resultValue.length,
            });
        }
    };

    handleShowGiftMessage = () => {
        const { showGiftMessage } = this.state;
        this.setState({
            showGiftMessage: !showGiftMessage,
        });
    };

    handleToggleSuggestedMessages = () => {
        const { showSuggestedMessages, activePanel } = this.state;
        this.setState({
            showSuggestedMessages: !showSuggestedMessages,
            activePanel: activePanel === 1 ? 0 : 0,
        });
    };

    handleSelectOccasion = (i, occasionCode) => {
        this.setState({
            occasionIndex: i,
            activePanel: 1,
            occasionCode,
        });
    };

    handleBackToOccasions = () => {
        this.setState({
            activePanel: 0,
        });
    };

    handleAddMessage = (message) => {
        const { callHandleChange } = this.props;
        const { textField, showSuggestedMessages, occasionCode } = this.state;
        if (textField !== message) {
            this.setState({
                textField: message,
                characterCount: message.length,
                showSuggestedMessages: !showSuggestedMessages,
            });

            callHandleChange({ name: 'message', value: message });
            callHandleChange({ name: 'occasion', value: occasionCode });
        }
    };

    /**
     * @description sets default message for sci channel
     */
    addDefaultMessage = () => {
        const { savedAddress, callHandleChange } = this.props;
        const { occasionCode } = this.state;
        const decedentName = savedAddress?.addressComponents?.DecedentFullName || null;
        let message = '';

        if (decedentName) {
            message = `In loving memory of ${decedentName}.`;
            this.setState({
                textField: message,
            });

            callHandleChange({ name: 'message', value: message, occasionCode });
        }
    }

    render() {
        const {
            textField,
            characterCount,
            maxCharacters,
            showGiftMessage,
            showSuggestedMessages,
            occasionIndex,
            activePanel,
        } = this.state;
        const { classes, dataDictionary } = this.props;
        const language = getLanguageName();

        return (
            <div className={classes.root}>
                <FormControlLabel
                    label={
                        language === '/en'
                            ? 'Add card message (optional)' : dataDictionary['Add card message (optional)']
                    }
                    classes={{ label: classes.formCheckBoxLabel }}
                    control={(
                        <Checkbox
                            id="optional-messsage"
                            data-test="pdp-optional-message"
                            data-testid="pdp-optional-message"
                            name="pdpMessage"
                            disableRipple
                            value="checkedB"
                            color="primary"
                            onChange={this.handleShowGiftMessage}
                            classes={{
                                root: classes.checkbox,
                                checked: classes.checked,
                            }}
                        />
                    )}
                />
                <div
                    className={
                        showGiftMessage
                            ? classes.displayBlock
                            : classes.displayNone
                    }
                >
                    <div className={classes.messageHeader}>
                        <div />
                        <OccasionsDialog
                            showSuggestedMessages={showSuggestedMessages}
                            occasionIndex={occasionIndex}
                            activePanel={activePanel}
                            handleToggleSuggestedMessages={this.handleToggleSuggestedMessages}
                            handleSelectOccasion={this.handleSelectOccasion}
                            handleBackToOccasions={this.handleBackToOccasions}
                            handleAddMessage={this.handleAddMessage}
                        />
                    </div>
                    <div>
                        <Paper className={classes.paperBody} elevation={1}>
                            <TextField
                                id="filled-multiline-static"
                                data-test="pdp-filled-multiline-static"
                                data-testid="pdp-filled-multiline-static"
                                name="giftMessage"
                                label={`${maxCharacters
                                    - characterCount} ${dataDictionary?.['Characters Left'] || 'Characters Left'}`}
                                multiline
                                rows="4"
                                value={textField}
                                onChange={this.handleChange}
                                className={classes.textField}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Paper>
                    </div>
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    callResetGiftMessageForm: bindActionCreators(resetGiftMessageForm, dispatch),
    callHandleChange: bindActionCreators(handleChange, dispatch),
});

const mapStateToProps = (state) => ({
    savedAddress: getSavedAddress(state),
    dataDictionary: getDataDictionary(state),
});

GraphqlPDPGiftMessage.defaultProps = {
    savedAddress: {
        addressComponents: {
            DecedentFullName: '',
        },
        addressSource: '',
    },
};

GraphqlPDPGiftMessage.propTypes = {
    classes: object.isRequired,
    callResetGiftMessageForm: func.isRequired,
    callHandleChange: func.isRequired,
    savedAddress: shape({
        addressComponents: shape({
            DecedentFullName: string,
        }),
        addressSource: string,
    }),
    dataDictionary: object.isRequired,
};

export default compose(
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
)(GraphqlPDPGiftMessage);
